<template>
  <section id="hero" class="section-shell hero-section" data-theme="portal">
    <div class="hero-copy" data-reveal>
        <h1>
          As <span class="word-brand">instituições</span> movem o mercado
          <span class="line-break">As <span class="word-brand">baleias</span> escrevem o roteiro</span>
          <span class="line-break emphasis"><span class="word-brand">Você</span> só precisa aprender a ler</span>
        </h1>
        <p class="lead">
          Bem-vindo à <span class="word-brand">ZIMA</span>.<br />
          Onde a manipulação vira mapa e a leitura vira vantagem.
        </p>
        <button
          class="cta primary"
          @click="$emit('cta')"
          @mousemove="magnetic"
          @mouseleave="resetMagnetic"
          :style="magneticStyle"
        >
          Quero ler o jogo antes que ele aconteça
        </button>
        <div class="hero-hint">Direção clara. Execução objetiva. Sem ruído.</div>
        <div class="hero-signals">
          <span v-for="signal in signals" :key="signal" class="signal-chip">{{ signal }}</span>
        </div>
        <button class="scroll-cue" type="button" aria-label="Ver planos" @click="scrollNext">
          <span class="chevron" aria-hidden="true"></span>
        </button>
      </div>
    <div class="hero-visual" data-reveal aria-hidden="true">
      <canvas ref="heroCanvas" class="hero-canvas" aria-hidden="true"></canvas>
    </div>
  </section>
</template>

<script setup>
import { computed, defineProps, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
  reduceMotion: {
    type: Boolean,
    default: false,
  },
});

const signals = [
  'Setups com invalidação clara',
  'Mapa macro diário',
  'Risco definido antes da entrada',
];

const scrollNext = () => {
  const target = document.getElementById('oferta2');
  if (!target) return;
  target.scrollIntoView({ behavior: props.reduceMotion ? 'auto' : 'smooth', block: 'start' });
};

const heroCanvas = ref(null);
const pointerOffset = ref({ x: 0, y: 0 });
let frameId = null;
let resizeHandler = null;
let ctx = null;
let lines = [];
let particles = [];

const magneticStyle = computed(() => ({
  transform: `translate(${pointerOffset.value.x}px, ${pointerOffset.value.y}px)`,
}));

const magnetic = (event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width - 0.5) * 12;
  const y = ((event.clientY - rect.top) / rect.height - 0.5) * 8;
  pointerOffset.value = { x, y };
};

const resetMagnetic = () => {
  pointerOffset.value = { x: 0, y: 0 };
};

const isMobileLayout = (width) => width < 768;

const particleSpawnY = (width, height) => (
  isMobileLayout(width)
    ? height * 0.44 + Math.random() * height * 0.5
    : height * 0.16 + Math.random() * height * 0.68
);

const initScene = (width, height) => {
  const mobile = isMobileLayout(width);
  const topBand = height * (mobile ? 0.48 : 0.24);
  const bottomBand = height * (mobile ? 0.94 : 0.76);
  lines = Array.from({ length: 14 }, (_, index) => ({
    amp: 8 + index * 3.4,
    freq: 0.0085 + index * 0.00095,
    phase: Math.random() * Math.PI * 2,
    speed: 0.0048 + index * 0.00072,
    palette: index % 2 === 0 ? 'blue' : 'purple',
    centerY: topBand + ((bottomBand - topBand) / 13) * index,
  }));

  particles = Array.from({ length: mobile ? 70 : 120 }, () => ({
    x: Math.random() * width,
    y: particleSpawnY(width, height),
    r: 0.8 + Math.random() * 2.9,
    vx: 0.18 + Math.random() * 0.9,
    alpha: 0.14 + Math.random() * 0.45,
  }));
};

const draw = () => {
  if (!ctx || !heroCanvas.value) return;
  const width = heroCanvas.value.clientWidth;
  const height = heroCanvas.value.clientHeight;

  ctx.clearRect(0, 0, width, height);

  lines.forEach((line) => {
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    if (line.palette === 'blue') {
      gradient.addColorStop(0, 'rgba(115, 148, 240, 0.06)');
      gradient.addColorStop(0.28, 'rgba(115, 148, 240, 0.12)');
      gradient.addColorStop(0.52, 'rgba(115, 148, 240, 0.34)');
      gradient.addColorStop(1, 'rgba(115, 148, 240, 0.74)');
    } else {
      gradient.addColorStop(0, 'rgba(155, 89, 182, 0.05)');
      gradient.addColorStop(0.28, 'rgba(155, 89, 182, 0.11)');
      gradient.addColorStop(0.52, 'rgba(155, 89, 182, 0.3)');
      gradient.addColorStop(1, 'rgba(155, 89, 182, 0.62)');
    }

    ctx.beginPath();
    ctx.lineWidth = 1.15;
    ctx.strokeStyle = gradient;
    for (let x = 0; x <= width; x += 3) {
      const y =
        line.centerY +
        Math.sin(x * line.freq + line.phase) * line.amp +
        Math.sin(x * line.freq * 0.38 + line.phase * 0.6) * (line.amp * 0.35);
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    line.phase += line.speed;
  });

  particles.forEach((p) => {
    p.x += p.vx;
    if (p.x > width + 8) {
      p.x = -10;
      p.y = particleSpawnY(width, height);
    }
    const leftFade = Math.max(0.08, Math.min(1, (p.x - width * 0.08) / (width * 0.74)));
    ctx.beginPath();
    ctx.fillStyle = `rgba(178, 208, 255, ${p.alpha * leftFade})`;
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fill();
  });

  if (!props.reduceMotion) {
    frameId = requestAnimationFrame(draw);
  }
};

const fitCanvas = () => {
  if (!heroCanvas.value) return;
  const el = heroCanvas.value;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = Math.max(1, el.clientWidth);
  const h = Math.max(1, el.clientHeight);
  el.width = Math.round(w * dpr);
  el.height = Math.round(h * dpr);
  ctx = el.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  initScene(w, h);
  draw();
};

onMounted(() => {
  fitCanvas();
  resizeHandler = () => {
    if (frameId) cancelAnimationFrame(frameId);
    fitCanvas();
  };
  window.addEventListener('resize', resizeHandler);
});

onBeforeUnmount(() => {
  if (resizeHandler) window.removeEventListener('resize', resizeHandler);
  if (frameId) cancelAnimationFrame(frameId);
});
</script>

<style scoped>
.hero-section {
  position: relative;
  min-height: calc(100svh - 156px);
  padding-top: clamp(18px, 2.2vw, 26px);
  padding-bottom: clamp(14px, 1.9vw, 22px);
  display: flex;
  align-items: center;
  overflow: hidden;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.hero-section,
.hero-section * {
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif !important;
}

.hero-copy {
  position: relative;
  z-index: 2;
  width: min(64%, 880px);
}

.hero-copy h1 {
  position: relative;
  z-index: 2;
  font-size: clamp(44px, 4.8vw, 78px);
  line-height: 0.98;
  color: #edf3ff;
  -webkit-text-stroke: 0;
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08),
    0 10px 24px rgba(8, 14, 30, 0.34);
  margin-bottom: 12px;
  letter-spacing: -0.03em;
}

.hero-copy .line-break {
  display: block;
  margin-top: 0.32em;
}

.hero-copy .emphasis {
  color: #dbe5ff;
}

.hero-copy .word-brand {
  background: linear-gradient(120deg, #6fa6ff 0%, #bd8bff 45%, #7ea5ff 75%, #d2b2ff 100%);
  background-size: 220% 220%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 14px rgba(127, 166, 255, 0.28);
  animation: zima-brand-shift 4.2s ease-in-out infinite;
  will-change: background-position;
}

.hero-copy .lead {
  font-size: clamp(15px, 1.2vw, 19px);
  line-height: 1.55;
  margin: 8px 0 18px;
  color: var(--zima-muted);
  max-width: 48ch;
}

.hero-hint {
  margin-top: 24px;
  font-family: var(--zima-font-mono);
  font-size: 11px;
  font-style: italic;
  letter-spacing: 0.09em;
  color: var(--zima-subtle);
}

.hero-visual {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.hero-signals,
.scroll-cue {
  display: none;
}

.hero-canvas {
  width: 100%;
  height: 100%;
  display: block;
  opacity: 0.9;
}

.cta.primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  padding: 14px 20px;
  border-radius: 999px;
  border: 1px solid rgba(115, 148, 240, 0.55);
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.92), rgba(155, 89, 182, 0.92), rgba(78, 115, 223, 0.92));
  color: var(--zima-foreground);
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow:
    0 0 0 1px rgba(115, 148, 240, 0.28) inset,
    0 18px 48px rgba(20, 40, 80, 0.6),
    0 0 24px rgba(115, 148, 240, 0.34);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.cta.primary:hover {
  cursor: pointer;
  box-shadow:
    0 0 0 1px rgba(170, 191, 255, 0.52) inset,
    0 20px 50px rgba(10, 20, 40, 0.72),
    0 0 26px rgba(155, 89, 182, 0.35);
}

@keyframes zima-brand-shift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@media (max-width: 1160px) {
  .hero-section {
    min-height: calc(100svh - 140px);
  }

  .hero-copy {
    width: min(68%, 760px);
  }

  .hero-copy h1 {
    font-size: clamp(36px, 8vw, 58px);
    line-height: 1.01;
  }
}

@media (max-width: 767px) {
  .hero-section {
    min-height: auto;
    padding-top: 26px;
    padding-bottom: 30px;
  }

  .hero-copy {
    width: 100%;
  }

  .hero-copy h1 {
    font-size: clamp(30px, 8.4vw, 42px);
    line-height: 1.04;
  }

  .hero-visual {
    position: absolute;
    inset: 0;
    opacity: 0.65;
    -webkit-mask-image: linear-gradient(180deg, transparent 6%, rgba(0, 0, 0, 0.45) 40%, #000 72%);
    mask-image: linear-gradient(180deg, transparent 6%, rgba(0, 0, 0, 0.45) 40%, #000 72%);
  }

  .hero-canvas {
    height: 100%;
  }

  .hero-signals {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 24px;
    position: relative;
    z-index: 2;
  }

  .signal-chip {
    padding: 7px 12px 7px 13px;
    border-radius: 2px 10px 2px 10px;
    border: 1px solid rgba(130, 164, 246, 0.26);
    border-left: 2px solid rgba(143, 187, 255, 0.85);
    background: linear-gradient(90deg, rgba(78, 115, 223, 0.16), rgba(10, 14, 26, 0.62) 65%);
    color: #c7d5f2;
    font-family: var(--zima-font-mono) !important;
    font-size: 10.5px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .scroll-cue {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin: 22px auto 0;
    padding: 0;
    border-radius: 999px;
    border: 1px solid rgba(130, 164, 246, 0.32);
    background: rgba(10, 14, 26, 0.55);
    cursor: pointer;
    position: relative;
    z-index: 2;
    animation: cue-bob 2.1s ease-in-out infinite;
  }

  .scroll-cue .chevron {
    width: 9px;
    height: 9px;
    border-right: 2px solid #9fb6e8;
    border-bottom: 2px solid #9fb6e8;
    transform: rotate(45deg);
    margin-top: -4px;
  }
}

@keyframes cue-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(5px); }
}

@media (prefers-reduced-motion: reduce) {
  .hero-copy h1 { text-shadow: none; }
}
</style>
